import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { API_BASE_URL } from '../injection-tokens';
import { AuthStatus } from '../models/auth-status.model';
import { UserInfo } from '../models/user-info.model';
import { PARTNER_DEALS_STORAGE_KEY } from '../models/storage-keys';
@Injectable({
  providedIn: 'root',
})
export class AuthOtpService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly authenticationBaseUrl = `${this.apiBaseUrl}/v1/authentication`;

  isLoggedIn(): Observable<boolean> {
    return this.getAuthStatus().pipe(
      map((response) => response?.isAuthenticated === true),
      catchError(() => of(false)),
    );
  }

  getAuthStatus(): Observable<AuthStatus> {
    return this.http.get<AuthStatus>(`${this.authenticationBaseUrl}/status`, { withCredentials: true });
  }

  getUserInfo(): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${this.authenticationBaseUrl}/userinfo`, { withCredentials: true });
  }

  signIn(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.authenticationBaseUrl}/signin`, { email });
  }

  verify(email: string, otp: string, redirectUrl: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.authenticationBaseUrl}/verify`, { email, otp, redirectUrl });
  }

  logout() {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem(PARTNER_DEALS_STORAGE_KEY);

      const redirectUrl = globalThis.location.origin;
      const homeUrl = this.localizeUrl('/');

      this.http
        .post(`${this.authenticationBaseUrl}/signout`, { redirectUrl }, { withCredentials: true })
        .subscribe({
          next: () => {
            globalThis.location.href = homeUrl;
          },
          error: () => {
            globalThis.location.href = homeUrl;
          },
        });
    }
  }

  redirectToLogin() {
    if (isPlatformBrowser(this.platformId)) {
      const currentUrl = globalThis.location.href;

      const loginPath = `/auth/login?redirectUrl=${encodeURIComponent(currentUrl)}`;
      const loginUrl = this.localizeUrl(loginPath);

      globalThis.location.href = loginUrl;
    }
  }

  private localizeUrl(path: string): string {
    if (!isPlatformBrowser(this.platformId)) {
      return path;
    }

    const firstSegment = globalThis.location.pathname.split('/').find(Boolean) || '';

    // Verify if the first segment is a language prefix (e.g., 'en', 'nl', 'en-GB', 'nl-BE')
    const isLangPrefix = /^[a-z]{2}(-[a-z]{2,4})?$/i.test(firstSegment);
    const langPrefix = isLangPrefix ? firstSegment : '';

    const cleanPath = path.startsWith('/') ? path : `/${path}`;

    return langPrefix ? `/${langPrefix}${cleanPath}` : cleanPath;
  }
}
